// @ts-check
/** @typedef {import('../types/api').Collection} Collection */

import { Edit2, Eye, Plus, RefreshCw, Search, Sparkles, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import ImageUploadPreview from '../components/ImageUploadPreview';
import LoadingScreen from '../components/LoadingScreen';
import { focalToObjectPosition, toFocal } from '../components/ui/FocalPointPicker';
import Modal from '../components/ui/Modal';
import adminCollectionService from '../services/adminCollectionService';

const CollectionsPage = () => {
  /** @type {[Collection[], Function]} */
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [error, setError] = useState('');

  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      const params = searchTerm ? { search: searchTerm } : {};
      const data = await adminCollectionService.getCollections(params);
      setCollections(data);
      setError('');
    } catch (err) {
      setError('Failed to load collections');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const handleTogglePublish = async (collection) => {
    try {
      await adminCollectionService.togglePublish(collection.id, !collection.is_published);
      loadCollections();
    } catch (err) {
      alert('Failed to update publish status');
    }
  };

  const handleDelete = async () => {
    try {
      await adminCollectionService.deleteCollection(selectedCollection.id);
      setShowDeleteModal(false);
      setSelectedCollection(null);
      loadCollections();
    } catch (err) {
      alert('Failed to delete collection');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif">Collections</h1>
        <div className="flex gap-3">
          <button
            onClick={loadCollections}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-collection-btn"
            className="bg-primary text-primary-foreground px-6 py-2 flex items-center gap-2 hover:bg-primary/90 transition"
          >
            <Plus size={20} />
            ADD COLLECTION
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-3 text-muted-foreground/70" size={20} />
        <input
          type="text"
          placeholder="Search collections..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-collections-input"
          className="w-full pl-10 pr-4 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/50 border rounded-lg border-red-200 dark:border-red-900 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Collections Grid */}
      {loading ? (
        <LoadingScreen />
      ) : collections.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No collections found. Create your first collection!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map((collection) => (
            <div
              key={collection.id}
              className="border rounded-lg border-border bg-card overflow-hidden group hover:shadow-lg transition"
            >
              {/* Collection Image */}
              <div className="aspect-[4/3] bg-muted relative">
                {collection.hero_image ? (
                  <img
                    src={collection.hero_image}
                    alt={collection.name}
                    className="w-full h-full object-cover"
                    style={{
                      objectPosition: focalToObjectPosition(toFocal(collection.hero_focal_x, collection.hero_focal_y)),
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/70">
                    No Image
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span
                    className={`px-3 py-1 text-xs font-medium ${
                      collection.is_published
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    {collection.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>

              {/* Collection Info */}
              <div className="p-4">
                <h3 className="font-serif text-xl mb-2">{collection.name}</h3>
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                  {collection.description || 'No description'}
                </p>

                {/* Product Count */}
                {(collection.active_products_count > 0 || collection.inactive_products_count > 0) && (
                  <p className="text-xs text-muted-foreground mb-4">
                    <span className="font-medium">{collection.active_products_count} active</span>
                    {collection.inactive_products_count > 0 && (
                      <>
                        ,{' '}
                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                          {collection.inactive_products_count} inactive
                        </span>
                      </>
                    )}{' '}
                    product{collection.active_products_count + collection.inactive_products_count !== 1 ? 's' : ''}
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedCollection(collection);
                      setShowEditModal(true);
                    }}
                    data-testid={`edit-collection-${collection.id}`}
                    className="flex-1 px-3 py-2 border rounded-lg border-input hover:bg-muted/50 flex items-center justify-center gap-2 text-sm"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleTogglePublish(collection)}
                    data-testid={`toggle-publish-${collection.id}`}
                    className="flex-1 px-3 py-2 border rounded-lg border-input hover:bg-muted/50 flex items-center justify-center gap-2 text-sm"
                  >
                    <Eye size={16} />
                    {collection.is_published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCollection(collection);
                      setShowDeleteModal(true);
                    }}
                    data-testid={`delete-collection-${collection.id}`}
                    className="px-3 py-2 border rounded-lg border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center justify-center"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modals */}
      {showAddModal && <CollectionModal onClose={() => setShowAddModal(false)} onSave={loadCollections} />}

      {showEditModal && selectedCollection && (
        <CollectionModal
          collection={selectedCollection}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCollection(null);
          }}
          onSave={loadCollections}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCollection && (
        <Modal
          maxWidth="md"
          fullHeight={false}
          className="p-6"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedCollection(null);
          }}
        >
          <h3 className="text-xl font-serif mb-4">Delete Collection</h3>
          <p className="text-muted-foreground mb-6">
            Are you sure you want to delete "{selectedCollection.name}"? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedCollection(null);
              }}
              data-testid="cancel-delete-btn"
              className="px-4 py-2 border rounded-lg border-input hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
              className="px-4 py-2 bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Collection Add/Edit Modal Component
// Where the storefront crops a collection's desktop hero image (the detail page shows it uncropped).
const COLLECTION_HERO_PREVIEWS = [
  { label: 'Homepage carousel 4:5', ratio: '4 / 5' },
  { label: 'Collection showcase 1:1', ratio: '1 / 1' },
];

const CollectionModal = ({ collection = null, onClose, onSave }) => {
  // Touched-since-open flag, reset after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    name: collection?.name || '',
    slug: collection?.slug || '',
    description: collection?.description || '',
    hero_image: collection?.hero_image || '',
    hero_focal_x: collection?.hero_focal_x ?? null,
    hero_focal_y: collection?.hero_focal_y ?? null,
    mobile_hero_image: collection?.mobile_hero_image || '',
    is_published: collection?.is_published ?? true,
    display_order: collection?.display_order || 0,
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [generatingDescription, setGeneratingDescription] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const adminProductService = await import('../services/adminProductService').then((m) => m.default);

      const raw = await adminProductService.getAllProducts({ limit: 200 });
      const data = raw.items;
      setProducts(data);

      // Pre-select products that belong to this collection
      if (collection) {
        const collectionProducts = data.filter((p) => p.collection_id === collection.id);
        setSelectedProducts(collectionProducts.map((p) => p.id));
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  }, [collection]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Cancel dismisses the modal — confirm first if there's anything to lose.
  // The internal onClose() call after a successful save (end of
  // handleSubmit) bypasses this deliberately.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      slug: collection ? prev.slug : generateSlug(name),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      let collectionId;

      if (collection) {
        await adminCollectionService.updateCollection(collection.id, formData);
        collectionId = collection.id;
      } else {
        const newCollection = await adminCollectionService.createCollection(formData);
        collectionId = newCollection.id;
      }

      // Update product assignments
      const adminProductService = await import('../services/adminProductService').then((m) => m.default);
      for (const product of products) {
        const shouldBeInCollection = selectedProducts.includes(product.id);
        const isInCollection = product.collection_id === collectionId;

        if (shouldBeInCollection && !isInCollection) {
          // Add product to collection
          await adminProductService.updateProduct(product.id, { collection_id: collectionId });
        } else if (!shouldBeInCollection && isInCollection) {
          // Remove product from collection
          await adminProductService.updateProduct(product.id, { collection_id: null });
        }
      }

      setIsDirty(false);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save collection');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.name.trim()) {
      setError('Add a collection name first.');
      return;
    }
    if (formData.description && !window.confirm('This will replace the current description. Continue?')) return;

    setError('');
    setGeneratingDescription(true);
    try {
      const productNames = products.filter((p) => selectedProducts.includes(p.id)).map((p) => p.name);
      const result = await adminCollectionService.generateDescription({
        name: formData.name,
        product_names: productNames,
        hero_image_url: formData.hero_image || undefined,
      });
      setFormData((prev) => ({ ...prev, description: result.description || '' }));
    } catch (err) {
      setError(err.message || 'Failed to generate description');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleProductToggle = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  };

  return (
    <Modal
      maxWidth="3xl"
      onClose={onClose}
      className="px-6 pt-[50px] pb-[50px] rounded-[10px]"
      closeOnBackdrop={false}
      hasUnsavedChanges={isDirty}
    >
      <h2 className="text-2xl font-serif mb-6">{collection ? 'Edit Collection' : 'Add New Collection'}</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/50 border rounded-lg border-red-200 dark:border-red-900 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Collection Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleNameChange}
            required
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Slug *</label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            readOnly={!!collection}
            required
            className={`w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground ${
              collection ? 'bg-muted cursor-not-allowed' : ''
            }`}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {collection ? 'Slug cannot be changed after creation' : 'URL-friendly identifier (e.g., ready-to-wear)'}
          </p>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <label className="block text-sm font-medium">Description</label>
            <button
              type="button"
              onClick={handleGenerateDescription}
              disabled={generatingDescription}
              className="flex items-center gap-1.5 text-xs px-2 py-1 border border-input rounded-md hover:bg-muted/50 disabled:opacity-50"
              title="Generate description from the collection name, selected products, and hero image"
            >
              <Sparkles size={12} className={generatingDescription ? 'animate-pulse' : ''} />
              {generatingDescription ? 'Generating...' : 'Generate with AI'}
            </button>
          </div>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        </div>

        <div>
          <ImageUploadPreview
            value={formData.hero_image}
            onChange={(url) => setFormData({ ...formData, hero_image: url })}
            label="Hero Image (Desktop — landscape)"
            uploadType="collection-image"
            focal={toFocal(formData.hero_focal_x, formData.hero_focal_y)}
            onFocalChange={(f) => setFormData({ ...formData, hero_focal_x: f?.x ?? null, hero_focal_y: f?.y ?? null })}
            focalPreviews={COLLECTION_HERO_PREVIEWS}
          />
        </div>

        <div>
          <ImageUploadPreview
            value={formData.mobile_hero_image}
            onChange={(url) => setFormData({ ...formData, mobile_hero_image: url })}
            label="Mobile Hero Image (Portrait — shown on phones)"
            uploadType="collection-image"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Display Order
            <span
              className="text-xs text-muted-foreground ml-2"
              title="Collections with lower numbers appear first on the website"
            >
              ⓘ Lower numbers appear first
            </span>
          </label>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_published"
            name="is_published"
            checked={formData.is_published}
            onChange={handleChange}
            className="mr-2"
          />
          <label htmlFor="is_published" className="text-sm font-medium">
            Published (visible on website)
          </label>
        </div>

        {/* Product Assignment */}
        <div className="col-span-full border-t pt-4 mt-4">
          <label className="block text-sm font-medium mb-2">Assign Products ({selectedProducts.length} selected)</label>
          <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
            {products.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">No products available</p>
            ) : (
              <div className="divide-y">
                {products.map((product) => {
                  const isInactive = !product.is_published || product.is_deleted;
                  return (
                    <label
                      key={product.id}
                      className={`flex items-center p-3 hover:bg-muted/50 cursor-pointer ${
                        isInactive ? 'opacity-50 bg-muted' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleProductToggle(product.id)}
                        className="mr-3"
                      />
                      <div className="flex-1 flex items-center gap-3">
                        {product.image_urls?.[0] && (
                          <img src={product.image_urls[0]} alt={product.name} className="w-10 h-10 object-cover" />
                        )}
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            {product.name}
                            {isInactive && (
                              <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-md">
                                Inactive
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {'\u20B9'}
                            {Number(product.price).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Select products to include in this collection</p>
        </div>

        <div className="flex gap-3 justify-end pt-4">
          <button
            type="button"
            onClick={handleCloseClick}
            data-testid="cancel-collection-btn"
            className="px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-collection-btn"
            className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
          >
            {saving ? 'Saving...' : 'Save Collection'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CollectionsPage;
